import { useCallback, useEffect, useMemo, useState } from "react";
import { TextInput } from "../../text-input/TextInput";
import styles from "./index.module.scss";
import { Button } from "components/buttons/Button";
import CloseIcon from "assets/icons/close-blue.svg?react";
import { isNullOrWhitespace, shorten } from "helpers/strings";
import classnames from "classnames/bind";
import { useDebounce } from "hooks/useDebounce";
const cx = classnames.bind(styles);

type StringKeys<T> = {
    [K in keyof T]: T[K] extends string ? K : never;
}[keyof T];

interface BaseAutocompleteProps<T> {
    items: T[];
    itemIdKey: StringKeys<T>;
    itemLabelKey: StringKeys<T>;
    itemImageKey?: StringKeys<T>;
    assignedItems: T[];
    handleAssignItem: (item: T) => void;
    handleAddItem: (item: string) => unknown;
    handleRemoveItem: (item: string) => void;
    itemsLoading?: boolean;
    labels?: {
        appliedItemsLabel?: string;
        noItemsAppliedLabel?: string;
        inputLabel?: string;
        addLabel?: string;
    };
    variant: "add" | "search";
    /** Whether multiple items can be assigned */
    isMultiple?: boolean;
    id?: string;
}

export const BaseAutocomplete = <T,>({
    items,
    assignedItems,
    itemIdKey,
    itemLabelKey,
    itemImageKey,
    handleAssignItem,
    handleAddItem,
    handleRemoveItem,
    itemsLoading = false,
    labels: {
        appliedItemsLabel = "Applied Items",
        noItemsAppliedLabel = "No Items Applied",
        inputLabel = "Add Item",
        addLabel = "Add"
    } = {},
    variant,
    isMultiple = true,
    id
}: BaseAutocompleteProps<T>) => {
    const [assignmentInput, setAssignmentInput] = useState<string>("");
    const [hasSearched, setHasSearched] = useState<boolean>(false);

    const { value: debouncedAssignment, cancel: cancelDebounce } =
        useDebounce<string>(assignmentInput, 1000);

    useEffect(() => {
        if (variant !== "search") return;

        // Using the assignmentInput instead of the debounced value makes new search UI respond faster
        if (!assignmentInput || assignmentInput.length < 2) {
            setHasSearched(false);
            cancelDebounce();
            return;
        }
    }, [variant, assignmentInput, cancelDebounce]);

    useEffect(() => {
        if (variant !== "search") return;
        if (debouncedAssignment || !isNullOrWhitespace(debouncedAssignment)) {
            const response = handleAddItem(debouncedAssignment);

            // if the response is a promise, wait for it to resolve
            if (response instanceof Promise) {
                response.finally(() => setHasSearched(true));
            } else {
                setHasSearched(true);
            }
        }

        // Add handleAddItem to the dependency array will cause an infinite loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variant, debouncedAssignment]);

    const handleAddItemClick = useCallback(() => {
        const newItem = items.find(
            (item) =>
                item[itemLabelKey] && item[itemLabelKey] === assignmentInput
        );
        if (newItem) {
            handleAssignItem(newItem);
            setAssignmentInput("");
        } else if (variant === "add" && assignmentInput.trim() !== "") {
            handleAddItem(assignmentInput.trim());
            setAssignmentInput("");
        }
    }, [
        handleAssignItem,
        assignmentInput,
        items,
        itemLabelKey,
        variant,
        handleAddItem
    ]);

    const handleAssignedItemChange = useCallback(
        (itemId: string) => {
            const assignedItem = items.find(
                (item) => item[itemLabelKey] === itemId
            );

            if (assignedItem) {
                handleAssignItem(assignedItem);
            } else if (variant === "add" && itemId.trim() !== "") {
                handleAddItem(itemId.trim());
            }
        },
        [items, itemLabelKey, handleAssignItem, variant, handleAddItem]
    );

    const nonAssignedItems = useMemo(() => {
        return items.filter((item) => {
            return !assignedItems.some(
                (assignedItem) => assignedItem[itemIdKey] === item[itemIdKey]
            );
        });
    }, [items, assignedItems, itemIdKey]);

    const isDisabled = !isMultiple && assignedItems.length > 0;

    // Generate unique IDs for each instance of this component
    const instanceId = id || `autocomplete-${crypto.randomUUID()}`;
    const inputId = `input-${instanceId}`;
    const datalistId = `datalist-${instanceId}`;

    return (
        <div className={cx("base-autocomplete")}>
            <div className={cx("assigned-items-container")}>
                <h6>{appliedItemsLabel}</h6>
                {assignedItems.length === 0 ? (
                    <div className={cx("no-items-assigned")}>
                        <p>{noItemsAppliedLabel}</p>
                    </div>
                ) : (
                    <div className={cx("assigned-items")}>
                        {assignedItems.map((item) => {
                            return (
                                <Button
                                    onClick={(c) => {
                                        handleRemoveItem(c.currentTarget.value);
                                    }}
                                    type="assignment"
                                    value={item[itemIdKey] as string}
                                    iconRight={<CloseIcon />}
                                    key={item[itemIdKey] as string}
                                >
                                    {itemImageKey && item[itemImageKey] && (
                                        <img
                                            src={item[itemImageKey] as string}
                                            alt={item[itemLabelKey] as string}
                                        />
                                    )}
                                    <div className={cx("assignment-label")}>
                                        {shorten(
                                            item[itemLabelKey] as string,
                                            15
                                        )}
                                    </div>
                                </Button>
                            );
                        })}
                    </div>
                )}
            </div>
            {!isDisabled && (
                <fieldset>
                    <label htmlFor={inputId} className="sr-only">
                        {inputLabel}
                    </label>
                    <TextInput
                        id={inputId}
                        type="text"
                        autocomplete="off"
                        value={assignmentInput}
                        placeholder={!isDisabled ? inputLabel : ""}
                        onChange={(e) => {
                            if (e.nativeEvent instanceof InputEvent) {
                                // Typing in the input
                                setAssignmentInput(e.target.value);
                            } else {
                                // Selecting from the datalist
                                handleAssignedItemChange(e.target.value);
                                setAssignmentInput("");
                            }
                        }}
                        onKeyDown={(e) => {
                            if (!assignmentInput) return;

                            // Selecting an option from the datalist whose value matches
                            // exactly the search query does not trigger onChange, but
                            // does trigger onKeyDown. So we handle it here.
                            const isDatalistSelection =
                                e.key === "Unidentified" &&
                                nonAssignedItems.some(
                                    (item) =>
                                        item[itemLabelKey] === assignmentInput
                                );

                            if (isDatalistSelection || e.key === "Enter") {
                                e.preventDefault();
                                cancelDebounce();
                                handleAddItemClick();
                            }
                        }}
                        list={datalistId}
                        disabled={isDisabled}
                        inlineLoading={itemsLoading}
                    />
                    <datalist id={datalistId}>
                        {nonAssignedItems.map((item, index) => (
                            <option
                                key={`${index}-${item[itemIdKey]}`}
                                value={item[itemLabelKey] as string}
                            />
                        ))}
                    </datalist>
                    {assignmentInput.length >= 2 &&
                        hasSearched &&
                        nonAssignedItems.length === 0 &&
                        !itemsLoading && (
                            <p className={styles["no-results"]}>
                                No results found
                            </p>
                        )}
                    {variant === "add" && addLabel && handleAddItemClick && (
                        <div className={styles["add-button"]}>
                            <Button
                                onClick={handleAddItemClick}
                                disabled={assignmentInput === ""}
                                isInline={true}
                                type="text"
                            >
                                {addLabel}
                            </Button>
                        </div>
                    )}
                </fieldset>
            )}
        </div>
    );
};
